import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useToast } from "@/context/ToastContext";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { SearchSelect } from "@/components/ui/search-select";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Loader2, Plus, Trash2 } from "lucide-react";
import { formatINR } from "@/lib/utils";
import type { OrderRecord } from "@/types";

interface Props {
  order: OrderRecord | null;
  open: boolean;
  onClose: () => void;
  onSaved: () => void;
}

interface Row {
  key: string;
  id?: string;
  product_id?: string;
  product_name: string;
  unit: string;
  sent: number;
  quantity: string;
  unit_price: string;
  discount_pct: string;
  gst_rate: string;
  tolerance_pct: string;
  packing: string;
  delivery_date: string;
}

const num = (v: number | string | undefined | null) => Number(v || 0);
const cell = "h-8 text-xs px-2";

/** Change the quantities, rates, dates and packing of an order's lines, add a line, or take one out (if nothing was sent on it) */
export function AmendLinesDialog({ order, open, onClose, onSaved }: Props) {
  const { success, error } = useToast();
  const { hasPermission } = useAuth();
  const canReadProducts = hasPermission("product.read");
  const [rows, setRows] = useState<Row[]>([]);
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);
  const [pickKey, setPickKey] = useState(0);

  useEffect(() => {
    if (!open || !order) return;
    setReason("");
    setRows(
      (order.lines || []).map((l) => ({
        key: l.id,
        id: l.id,
        product_id: l.product_id || undefined,
        product_name: `${l.product_name}${l.variant_name ? ` · ${l.variant_name}` : ""}`,
        unit: l.unit,
        sent: num(l.dispatched_qty),
        quantity: String(num(l.quantity)),
        unit_price: String(num(l.unit_price)),
        discount_pct: String(num(l.discount_pct)),
        gst_rate: String(num(l.gst_rate)),
        tolerance_pct: l.tolerance_pct != null ? String(num(l.tolerance_pct)) : "",
        packing: l.packing || "",
        delivery_date: l.delivery_date ? l.delivery_date.slice(0, 10) : "",
      })),
    );
  }, [open, order]);

  const set = (key: string, patch: Partial<Row>) => setRows((rs) => rs.map((r) => (r.key === key ? { ...r, ...patch } : r)));

  const addProduct = (id: string, p: any | null) => {
    if (!id || !p) return;
    setRows((rs) => [
      ...rs,
      {
        key: `new-${Date.now()}`,
        product_id: id,
        product_name: p.name,
        unit: p.unit,
        sent: 0,
        quantity: "",
        unit_price: String(num(p.unit_price)),
        discount_pct: "0",
        gst_rate: String(num(p.gst_rate)),
        tolerance_pct: "",
        packing: "",
        delivery_date: "",
      },
    ]);
    setPickKey((k) => k + 1);
  };

  const total = rows.reduce((a, r) => a + num(r.quantity) * num(r.unit_price) * (1 - num(r.discount_pct) / 100), 0);

  const save = async () => {
    for (const r of rows) {
      if (!(num(r.quantity) > 0)) return error("Check the lines", `${r.product_name}: enter a quantity`);
      if (r.sent > num(r.quantity)) return error("Check the lines", `${r.product_name}: ${r.sent} ${r.unit} was already sent, so the quantity can't be lower`);
    }
    if (!reason.trim()) return error("Say why", "Add a reason for the amendment");
    setSaving(true);
    try {
      await api.put(`/api/SalesOrder/UpdateLines/${order!.id}`, {
        reason: reason.trim(),
        lines: rows.map((r) => ({
          id: r.id,
          product_id: r.id ? undefined : r.product_id,
          product_name: r.id ? undefined : r.product_name,
          unit: r.id ? undefined : r.unit,
          quantity: num(r.quantity),
          unit_price: num(r.unit_price),
          discount_pct: num(r.discount_pct),
          gst_rate: num(r.gst_rate),
          tolerance_pct: r.tolerance_pct === "" ? null : num(r.tolerance_pct),
          packing: r.packing.trim() || null,
          delivery_date: r.delivery_date || null,
        })),
      });
      success("Order amended", "");
      onSaved();
      onClose();
    } catch (err: any) {
      error("Could not amend the order", err.message || "Please try again");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && !saving && onClose()}>
      <DialogContent className="sm:max-w-5xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Amend lines · {order?.order_number}</DialogTitle>
          <DialogDescription>
            Quantity can't go below what was already sent, and a line that has been sent can't be removed. Changes are written to the deal's history.
          </DialogDescription>
        </DialogHeader>

        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="text-muted-foreground">
              <tr className="text-left">
                <th className="px-1 py-1.5 min-w-[160px]">Item</th>
                <th className="px-1 py-1.5 w-24">Quantity</th>
                <th className="px-1 py-1.5 w-24">Rate (₹)</th>
                <th className="px-1 py-1.5 w-16">Disc %</th>
                <th className="px-1 py-1.5 w-16">GST %</th>
                <th className="px-1 py-1.5 w-16">Tol ±%</th>
                <th className="px-1 py-1.5 min-w-[110px]">Packing</th>
                <th className="px-1 py-1.5 w-36">Delivery by</th>
                <th className="w-8" />
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.key} className="border-t align-top">
                  <td className="px-1 py-1.5">
                    <div className="font-medium text-sm">{r.product_name}</div>
                    <div className="text-[11px] text-muted-foreground">
                      {r.unit}
                      {r.sent > 0 ? ` · ${r.sent} sent` : ""}
                      {!r.id ? " · new line" : ""}
                    </div>
                  </td>
                  <td className="px-1 py-1.5"><Input className={cell} type="number" min={r.sent || 0} step="any" value={r.quantity} onChange={(e) => set(r.key, { quantity: e.target.value })} /></td>
                  <td className="px-1 py-1.5"><Input className={cell} type="number" min={0} step="any" value={r.unit_price} onChange={(e) => set(r.key, { unit_price: e.target.value })} /></td>
                  <td className="px-1 py-1.5"><Input className={cell} type="number" min={0} max={100} step="any" value={r.discount_pct} onChange={(e) => set(r.key, { discount_pct: e.target.value })} /></td>
                  <td className="px-1 py-1.5"><Input className={cell} type="number" min={0} step="any" value={r.gst_rate} onChange={(e) => set(r.key, { gst_rate: e.target.value })} /></td>
                  <td className="px-1 py-1.5"><Input className={cell} type="number" min={0} step="any" value={r.tolerance_pct} onChange={(e) => set(r.key, { tolerance_pct: e.target.value })} /></td>
                  <td className="px-1 py-1.5"><Input className={cell} value={r.packing} onChange={(e) => set(r.key, { packing: e.target.value })} /></td>
                  <td className="px-1 py-1.5"><Input className={cell} type="date" value={r.delivery_date} onChange={(e) => set(r.key, { delivery_date: e.target.value })} /></td>
                  <td className="px-1 py-1.5">
                    <Button
                      type="button"
                      size="icon"
                      variant="ghost"
                      className="h-8 w-8 text-red-600"
                      disabled={r.sent > 0 || rows.length === 1}
                      title={r.sent > 0 ? "Already dispatched, can't be removed" : rows.length === 1 ? "An order needs at least one line" : "Remove line"}
                      onClick={() => setRows((rs) => rs.filter((x) => x.key !== r.key))}
                    >
                      <Trash2 className="size-4" />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {canReadProducts && (
          <div className="space-y-1.5">
            <Label className="flex items-center gap-1.5"><Plus className="size-3.5" /> Add a product</Label>
            <SearchSelect
              key={pickKey}
              endpoint="/api/Product/List"
              extraParams={{ active: "true" }}
              value=""
              getLabel={(row) => `${row.name} (${row.code}) · ${formatINR(row.unit_price)}/${row.unit}`}
              placeholder="Search products"
              onChange={addProduct}
            />
          </div>
        )}

        <div className="space-y-1.5">
          <Label>Why is the order being amended? *</Label>
          <Textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={2} placeholder="e.g. Buyer asked for 500 m more and a later delivery by mail on 12 Oct" />
        </div>

        <DialogFooter className="items-center sm:justify-between">
          <span className="text-xs text-muted-foreground">Before GST: {formatINR(Math.round(total * 100) / 100)}</span>
          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose} disabled={saving}>Cancel</Button>
            <Button onClick={save} disabled={saving}>
              {saving && <Loader2 className="size-4 animate-spin mr-1.5" />} Save amendment
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
